import {
  createSlice,
  Draft,
  PayloadAction,
  Slice,
} from '@reduxjs/toolkit';
import { Opt } from 'app/tools/general.types';
import { WritableDraft } from 'immer/dist/types/types-external';
import { ApiError } from '../api-core.types';

type ApiState<T> = {
  value: T;
  loading: number;
  error?: ApiError;
};

export type ApiSlice<T, N extends string> = Slice<
  ApiState<T>,
  {
    start(state: WritableDraft<ApiState<T>>): void;
    success(
      state: WritableDraft<ApiState<T>>,
      action: PayloadAction<T>
    ): void;
    failure(
      state: WritableDraft<ApiState<T>>,
      action: PayloadAction<ApiError>
    ): void;
    end(state: WritableDraft<ApiState<T>>): void;
    set(
      state: WritableDraft<ApiState<T>>,
      action: PayloadAction<T>
    ): void;
    clearErrors(state: WritableDraft<ApiState<T>>): void;
  },
  N
>;

const apiSliceFactory =
  <T>() =>
  <N extends string>(name: N): ApiSlice<Opt<T>, N> => {
    const initialState: ApiState<Opt<T>> = {
      value: undefined,
      loading: 0,
      error: undefined,
    };

    return createSlice({
      name,
      initialState,

      reducers: {
        /* eslint-disable no-param-reassign */
        start(state) {
          state.loading += 1;
        },
        success(state, action: PayloadAction<T>) {
          state.value = action.payload as Draft<T>;
          state.error = undefined;
        },
        failure(state, action: PayloadAction<ApiError>) {
          state.error = action.payload as Draft<ApiError>;
        },
        end(state) {
          state.loading -= 1;
          state.loading = Math.max(state.loading, 0);
        },
        set(state, action: PayloadAction<Opt<T>>) {
          state.value = action.payload as Draft<T>;
        },
        clearErrors(state) {
          state.error = undefined;
        },
      } /* eslint-enable no-param-reassign */,
    });
  };

export default apiSliceFactory;
